import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  differenceInCalendarDays,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isSameDay,
  lastDayOfYear,
  startOfMonth,
  startOfYear,
  subMonths,
} from 'date-fns'

import { Option, OptionsList } from 'modules/core/components/DateNavigator/_OptionsList'
import { FixedDuration } from 'modules/core/components/DateNavigator/constants'
import { calculateRegularRange } from 'modules/core/components/DateNavigator/dateUtils'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import { DayPicker } from './_DayPicker'
import { FortNightPicker } from './_FortNightPicker'
import { MonthPicker } from './_MonthPicker'
import { QuarterPicker } from './_QuarterPicker'
import { RangePicker } from './_RangePicker'
import { WeekPicker } from './_WeekPicker'
import { YearPicker } from './_YearPicker'

export type PeriodOption =
  | 'day'
  | 'week'
  | 'fortnight'
  | 'month'
  | 'quarter'
  | 'year'
  | 'year_to_date'

const calculateCurrentPeriod = (
  startDate: Date,
  endDate: Date,
  regularPeriods?: FixedDuration[]
) => {
  if (!regularPeriods) {
    return undefined
  }

  if (isSameDay(startDate, endDate) && regularPeriods.includes('day')) {
    return 'day'
  }

  if (differenceInCalendarDays(endDate, startDate) === 6 && regularPeriods.includes('week')) {
    return 'week'
  }

  if (differenceInCalendarDays(endDate, startDate) === 13 && regularPeriods.includes('fortnight')) {
    return 'fortnight'
  }

  if (
    isFirstDayOfMonth(startDate) &&
    isLastDayOfMonth(endDate) &&
    isSameDay(startDate, startOfMonth(endDate)) &&
    regularPeriods.includes('month')
  ) {
    return 'month'
  }

  if (
    isFirstDayOfMonth(startDate) &&
    isLastDayOfMonth(endDate) &&
    isSameDay(startDate, startOfMonth(subMonths(endDate, 2))) &&
    regularPeriods.includes('quarter')
  ) {
    return 'quarter'
  }

  if (
    isSameDay(startDate, startOfYear(endDate)) &&
    isSameDay(endDate, lastDayOfYear(startDate)) &&
    regularPeriods.includes('year')
  ) {
    return 'year'
  }

  return regularPeriods[0]
}

type Props = {
  regularPeriods?: FixedDuration[]

  startDate: Date
  endDate: Date
  onDateRangeChange: (startDate: Date, endDate: Date) => void
}

export const PickerSelector = ({
  regularPeriods,
  startDate,
  endDate,
  onDateRangeChange,
}: Props) => {
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption | undefined>(() =>
    calculateCurrentPeriod(startDate, endDate, regularPeriods)
  )

  useUnsafeEffect(() => {
    const newSelectedPeriod = calculateCurrentPeriod(startDate, endDate, regularPeriods)

    setSelectedPeriod(newSelectedPeriod)
  }, [startDate, endDate, regularPeriods])

  const periodOptions = useUnsafeMemo(() => {
    if (!regularPeriods) {
      return []
    }

    const currentOptions: Option<PeriodOption>[] = []

    if (regularPeriods.includes('day')) {
      currentOptions.push({
        label: i18n.t('common.date_navigator.regular_periods.day'),
        value: 'day',
      })
    }

    if (regularPeriods.includes('week')) {
      currentOptions.push({
        label: i18n.t('common.date_navigator.regular_periods.week'),
        value: 'week',
      })
    }

    if (regularPeriods.includes('fortnight')) {
      currentOptions.push({
        label: i18n.t('common.date_navigator.regular_periods.fortnightly'),
        value: 'fortnight',
      })
    }

    if (regularPeriods.includes('month')) {
      currentOptions.push({
        label: i18n.t('common.date_navigator.regular_periods.month'),
        value: 'month',
      })
    }

    if (regularPeriods.includes('quarter')) {
      currentOptions.push({
        label: i18n.t('common.date_navigator.regular_periods.quarter'),
        value: 'quarter',
      })
    }

    if (regularPeriods.includes('year')) {
      currentOptions.push({
        label: i18n.t('common.date_navigator.regular_periods.year'),
        value: 'year',
      })
    }

    return currentOptions
  }, [regularPeriods])

  const handlePeriodSelected = useUnsafeCallback(
    (value: PeriodOption) => {
      const newRange = calculateRegularRange(value, startDate)

      onDateRangeChange(newRange[0], newRange[1])
      setSelectedPeriod(value)
    },
    [startDate, onDateRangeChange]
  )

  const showOptions = periodOptions.length > 1

  const renderPickerComponent = () => {
    switch (selectedPeriod) {
      case 'day':
        return (
          <DayPicker
            date={startDate}
            onDateRangeChange={onDateRangeChange}
            firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
          />
        )

      case 'week':
        return (
          <WeekPicker
            date={startDate}
            onDateRangeChange={onDateRangeChange}
            firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
          />
        )

      case 'fortnight':
        return (
          <FortNightPicker
            date={startDate}
            onDateRangeChange={onDateRangeChange}
            firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
          />
        )

      case 'month':
        return <MonthPicker date={startDate} onDateRangeChange={onDateRangeChange} />

      case 'quarter':
        return <QuarterPicker date={startDate} onDateRangeChange={onDateRangeChange} />

      case 'year':
        return <YearPicker date={startDate} onDateRangeChange={onDateRangeChange} />

      case undefined:
        return (
          <RangePicker
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={onDateRangeChange}
            firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
          />
        )
    }

    return null
  }

  return (
    <Box flexDirection="row">
      {showOptions && (
        <Box border={{ right: { width: 's1', color: 'grey400', style: 'solid' } }}>
          <OptionsList
            options={periodOptions}
            selectedOptionValue={selectedPeriod}
            onOptionSelected={handlePeriodSelected}
          />
        </Box>
      )}

      {renderPickerComponent()}
    </Box>
  )
}
